/** @file artActions: what the Art screen does to the puppet: fill, clear, copy, masks, pack, export, ask. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import type { EditorStore } from '../../state/editorTypes';
import type { ArtDeps, BriefInput, BulkOutcome, FitMode, SlotImage, SlotViewAngles } from './artTypes';
import { bulkFill } from './bulkFill';
import { copyBrief } from './copyBrief';
import { downloadBrief } from './downloadBrief';
import { imageOfSlot } from './imageOfSlot';
import { maskDownload } from './maskDownload';
import { saveDownload } from './saveDownload';
import { slotsJson } from './slotsJson';

/** The Art screen's operations; each reports its failure as one toast and resolves `false` (or nothing). */
export type ArtActions = {
  fill: (slot: string, png: Uint8Array, fit?: FitMode) => Promise<boolean>;
  clear: (slot: string) => Promise<boolean>;
  copyFrom: (slot: string, source: string) => Promise<boolean>;
  downloadMask: (slot: string, style: 'silhouette' | 'skeleton') => Promise<void>;
  packSheet: () => Promise<boolean>;
  exportSlots: (views: ReadonlyMap<string, SlotViewAngles>) => void;
  askAgent: (text: string) => Promise<void>;
  /** Uploads and fills many images in one batch (one undo step); `fit` marks the ones that are not the slot's size. */
  fillMany: (items: (SlotImage & { fit?: FitMode })[]) => Promise<BulkOutcome>;
  copyBrief: (input: BriefInput) => Promise<void>;
  downloadBrief: (input: BriefInput) => void;
};

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Runs `commands` as one batch; true when every reply is ok, then the lint is refreshed. */
async function run(store: EditorStore, commands: Command[], label: string): Promise<boolean> {
  try {
    const replies = await store.getState().dispatch(commands, label);
    const ok = replies.every((r) => r.ok);
    if (ok) await store.getState().refreshLint();
    return ok;
  } catch (e) {
    store.getState().notify('error', `${label}: ${messageOf(e)}`);
    return false;
  }
}

/** Uploads the PNG, then `slot.fill { slot, image, fit? }` alone in its batch, then rebuilds the preview. */
async function fill(store: EditorStore, deps: ArtDeps, slot: string, png: Uint8Array, fit?: FitMode): Promise<boolean> {
  const id = store.getState().puppet.id;
  if (id === null) return false;
  let image: string;
  try {
    image = (await deps.uploadImage(id, png)).id;
  } catch (e) {
    store.getState().notify('error', `Upload failed: ${messageOf(e)}`);
    return false;
  }
  const ok = await run(store, [{ name: 'slot.fill', input: fit === undefined ? { slot, image } : { slot, image, fit } }], `Fill ${slot}`);
  if (ok) await store.getState().refreshPreview();
  return ok;
}

/** Fills `slot` with the image already filling `source` (the mirrored twin); a toast when `source` is empty. */
async function copyFrom(store: EditorStore, slot: string, source: string): Promise<boolean> {
  const doc = store.getState().puppet.doc;
  const image = doc === null ? null : imageOfSlot(doc.puppet, source);
  if (image === null) {
    store.getState().notify('warning', `${source} has no image to copy`);
    return false;
  }
  return run(store, [{ name: 'slot.fill', input: { slot, image } }], `Copy ${source} to ${slot}`);
}

/** Copies the prompt to the clipboard (when the browser allows) and opens the Agent screen. */
async function askAgent(store: EditorStore, text: string): Promise<void> {
  const s = store.getState();
  if (s.puppet.id === null) return;
  try {
    await navigator.clipboard.writeText(text);
    s.notify('info', 'Prompt copied: paste it to your agent');
  } catch {
    s.notify('warning', 'Copy the prompt by hand: the clipboard is not available');
  }
  s.navigate({ screen: 'agent', puppetId: s.puppet.id });
}

/** Downloads `slots.json` for the open puppet. */
function exportSlots(store: EditorStore, views: ReadonlyMap<string, SlotViewAngles>): void {
  const doc = store.getState().puppet.doc;
  if (doc === null) return;
  saveDownload(`${doc.name}-slots.json`, { text: slotsJson(doc, views), mime: 'application/json' });
}

/** The Art operations over `store` (actions, state) and `deps` (upload, masks). */
export function artActions(store: EditorStore, deps: ArtDeps): ArtActions {
  return {
    fill: (slot, png, fit) => fill(store, deps, slot, png, fit),
    clear: (slot) => run(store, [{ name: 'slot.clear', input: { slot } }], `Clear ${slot}`),
    copyFrom: (slot, source) => copyFrom(store, slot, source),
    downloadMask: async (slot, style) => {
      const id = store.getState().puppet.id;
      if (id === null) return;
      await maskDownload(deps, id, slot, style).catch((e: unknown) => { store.getState().notify('error', `Mask: ${messageOf(e)}`); });
    },
    packSheet: async () => {
      const ok = await run(store, [{ name: 'sheet.pack', input: {} }], 'Pack sheet');
      if (ok) store.getState().notify('info', 'Sheets packed');
      return ok;
    },
    exportSlots: (views) => { exportSlots(store, views); },
    askAgent: (text) => askAgent(store, text),
    fillMany: (items) => bulkFill(store, deps, items),
    copyBrief: (input) => copyBrief(store, input),
    downloadBrief: (input) => { downloadBrief(store, input); },
  };
}
